import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportCategoryCard = ({ category, onSelect, isActive }) => {
  const getColorClasses = (color, isEnabled = true) => {
    if (!isEnabled) {
      return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
    
    switch (color) {
      case 'primary':
        return 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20';
      case 'success':
        return 'bg-success/10 text-success border-success/20 hover:bg-success/20';
      case 'warning':
        return 'bg-warning/10 text-warning border-warning/20 hover:bg-warning/20';
      case 'error':
        return 'bg-error/10 text-error border-error/20 hover:bg-error/20';
      case 'accent':
        return 'bg-accent/10 text-accent border-accent/20 hover:bg-accent/20';
      case 'secondary':
        return 'bg-secondary/10 text-secondary border-secondary/20 hover:bg-secondary/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20 hover:bg-muted/20';
    }
  };

  const handleSelect = () => {
    if (!category?.isEnabled) {
      // Show validation message instead of selecting
      return;
    }
    onSelect?.(category);
  };

  return (
    <div
      className={`relative border rounded-lg p-6 cursor-pointer transition-all duration-200 ${
        isActive 
          ? 'ring-2 ring-primary shadow-lg' 
          : 'hover:shadow-md'
      } ${getColorClasses(category?.color, category?.isEnabled)}`}
      onClick={handleSelect}
    >
      {/* Disabled overlay */}
      {!category?.isEnabled && (
        <div className="absolute inset-0 bg-muted/50 rounded-lg flex items-center justify-center">
          <div className="text-center p-4">
            <Icon name="AlertCircle" size={32} className="text-muted-foreground mx-auto mb-2" />
            <div className="text-sm font-medium text-muted-foreground mb-1">Setup Required</div>
            <div className="text-xs text-muted-foreground">
              {category?.validationMessage || 'Additional configuration needed'}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
          category?.isEnabled ? getColorClasses(category?.color) : 'bg-muted/20'
        }`}>
          <Icon 
            name={category?.icon || 'FileText'} 
            size={24} 
            className={category?.isEnabled ? '' : 'text-muted-foreground'}
          />
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${
            category?.isEnabled ? 'text-foreground' : 'text-muted-foreground'
          }`}>
            {category?.reportCount || 0}
          </div>
          <div className="text-xs text-muted-foreground">
            {category?.reportCount === 1 ? 'Report' : 'Reports'}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className={`text-lg font-semibold ${
          category?.isEnabled ? 'text-foreground' : 'text-muted-foreground'
        }`}>
          {category?.name}
        </h3>
        <p className={`text-sm ${
          category?.isEnabled ? 'text-muted-foreground' : 'text-muted-foreground/70'
        }`}>
          {category?.description}
        </p>
      </div>

      {/* Validation message for disabled categories */}
      {!category?.isEnabled && category?.validationMessage && (
        <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-warning mt-0.5 flex-shrink-0" />
            <div className="text-xs text-warning">
              {category?.validationMessage}
            </div>
          </div>
        </div>
      )}

      {/* Available reports list for enabled categories */}
      {category?.isEnabled && category?.reports && category?.reports?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Available Reports:</h4>
          <div className="space-y-1">
            {category?.reports?.slice(0, 3)?.map((report) => (
              <div key={report?.id} className="flex items-center space-x-2">
                <div className="w-1.5 h-1.5 rounded-full bg-current opacity-50"></div>
                <span className="text-xs text-muted-foreground">{report?.name}</span>
              </div>
            ))}
            {category?.reports?.length > 3 && (
              <div className="text-xs text-muted-foreground pl-3.5">
                +{category?.reports?.length - 3} more reports
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-border">
        <Button
          variant={category?.isEnabled ? "outline" : "ghost"}
          size="sm"
          className="w-full"
          disabled={!category?.isEnabled}
          iconName={category?.isEnabled ? "ArrowRight" : "Lock"}
        >
          {category?.isEnabled ? 'View Reports' : 'Setup Required'}
        </Button>
      </div>
    </div>
  );
};

export default ReportCategoryCard;